import React, {useEffect, useState} from 'react'
import {PersonnelPageTitle} from '../../../../_metronic/layout/core/PersonnelPageData'
import {useAuth} from '../../../modules/auth'
import db from '../../../../services/firebase'
import {PageLink} from '../../../../_metronic/layout/core'
import {KTCard} from '../../../../_metronic/helpers'
import {AddPersonnelChecklistModal} from '../components/AddPersonnelChecklist'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import {EditPersonnelChecklistModal} from '../components/EditPersonnelChecklist'
import {format, parseISO} from 'date-fns'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Searchbar from 'app/components/Searchbar'
import useAuditContext from 'app/Hooks/useAuditContext'

const usersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]
type UserData = {
  uid: string
  id: string
  fullName: string
  role: string
  tenantId: string
  policiesAccepted?: boolean
  checklist_name: string
  createdon: string
  file_url: string
}

interface Group {
  id: string
  checklist: string
}

const PersonnelChecklist = () => {
  const [users, setUsers] = useState<UserData[]>([])
  const {currentUser} = useAuth()
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredItems, setFilteredItems] = useState<UserData[]>([])
  const [checklistCounts, setChecklistCounts] = useState<{[key: string]: number}>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [selectedData, setSelectedData] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [totalChecklist, setTotalChecklist] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<UserData | null>(null)
  
  // Bulk removal confirmation modal state
  const [isBulkRemoveModalOpen, setIsBulkRemoveModalOpen] = useState(false)
  const [isBulkRemoving, setIsBulkRemoving] = useState(false)
  
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    setFilteredItems(users)
  }, [users])

  useEffect(() => {
    getPersonnelChecklist()
  }, [])

  useEffect(() => {
    if (!currentUser?.tenantId) {
      return
    }

    const fetchGroups = async () => {
      const tenantId = currentUser.tenantId
      const tenantRef = doc(db, 'tenants', tenantId!)
      const policyRef = collection(tenantRef, 'personnel_group')

      const unsubscribe = onSnapshot(policyRef, (snapshot) => {
        const groupsData = snapshot.docs.map((doc) => ({
          ...(doc.data() as Group),
          id: doc.id,
        }))

        const groupCounts = groupsData.reduce((counts, group) => {
          const checklistName = group.checklist || '[Default]'
          counts[checklistName] = (counts[checklistName] || 0) + 1
          return counts
        }, {})

        setChecklistCounts(groupCounts)
      })

      return () => unsubscribe()
    }

    fetchGroups()
  }, [currentUser?.tenantId])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(users)
      setSearchLength(false)
    } else {
      const filteredItems = users.filter(
        (user) => user.checklist_name && user.checklist_name.toLowerCase().includes(searchTerm)
      )
      setFilteredItems(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [searchTerm, users])

  const getPersonnelChecklist = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'personnel_checklist')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs.map((doc) => ({
          ...(doc.data() as UserData),
          id: doc.id,
        }))
        setUsers(risksData)
        setTotalChecklist(risksData.length)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    }
  }

  const formatTimestamp = (timestamp) => {
    if (timestamp) {
      let date
      if (typeof timestamp === 'string') {
        if (timestamp.includes('-')) {
          date = parseISO(timestamp)
        } else if (timestamp.includes('/')) {
          const parts = timestamp.split('/')
          const month = +parts[0]
          const day = +parts[1]
          const year = +parts[2]
          date = new Date(year, month - 1, day)
        }
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000)
      } else {
        date = new Date(timestamp)
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Timestamp'
      }

      return format(date, 'd MMMM, yyyy')
    }
    return '--'
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleDeselectClick = () => {
    setCheckedItems([])
  }

  const handleCheckedRemoveClick = () => {
    setIsBulkRemoveModalOpen(true)
  }

  const handleConfirmBulkRemove = async () => {
    try {
      setIsBulkRemoving(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'personnel_checklist')
      const querySnapshot = await getDocs(policyRef)

      const documentsToDelete = querySnapshot.docs.filter(
        (doc) => doc.data().checklistName !== '[Default]'
      )

      if (documentsToDelete.length > 0) {
        const deletePromises = documentsToDelete.map((doc) => deleteDoc(doc.ref))
        await Promise.all(deletePromises)

        showSuccessToast({message: 'All checklists except "Default" have been deleted.'})
        setCheckedItems([])
      }
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the checklist.'})
    } finally {
      setDropdownVisible(false)
      setIsBulkRemoveModalOpen(false)
      setIsBulkRemoving(false)
    }
  }

  const handleDeleteClick = async (item: UserData) => {
    try {
      const count = checklistCounts[item.checklist_name] || 0
      if (count > 0) {
        showErrorToast({
          message:
            'This checklist has more than one group. Please remove the groups before deleting the checklist.',
        })
        return
      }
      if (totalChecklist <= 1) {
        showErrorToast({
          message: 'Cannot delete the last checklist. At least one checklist must remain.',
        })
      }

      setSelectedItem(item)
      setIsDeleteModalOpen(true)
      setCheckedItems([])
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the checlist.'})
    } finally {
      setDropdownVisible(false)
      setCheckedItems([])
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedItem) return

    setIsDeleting(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'personnel_checklist')
      const docRef = doc(policyRef, selectedItem.id)
      await deleteDoc(docRef)

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()

      const todaysDate = month + '/' + day + '/' + year

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `Checklist '${selectedItem?.checklist_name}' has been deleted by ${currentUser?.userName}'`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        action: 'Checklist deleted',
        target: 'Employees',
        date: todaysDate,
      })

      showSuccessToast({message: 'Checklist deleted successfully'})

      setIsDeleteModalOpen(false)
      setSelectedItem(null)
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the checklist.'})
    } finally {
      setIsDeleting(false)
    }
  }

  const handleEditClick = (id) => {
    setSelectedData(id)
    setIsModalOpen(true)
  }

  return (
    <>
      <PersonnelPageTitle
        icon='Icon.svg'
        title='Employees'
        // desc={totalPersonnel.toString() + ' Checklist'}
        desc='Manage your employees to ensure compliance and simplify audits'
        breadcrumbs={usersBreadcrumbs}
      >
        Personnel Management
      </PersonnelPageTitle>
      <EditPersonnelChecklistModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        id={selectedData}
      />
      <KTCard>
        <div>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              <div className='flex-lg-fill'>
                <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              </div>
            </div>
            <div className='card-toolbar'>
              {checkedItems.length > 0 ? (
                <>
                  {checkedItems.length > 0 ? (
                    <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                      <span>
                        <p className='pt-4'>{checkedItems.length} Selected</p>
                      </span>
                    </div>
                  ) : null}

                  <div className='mx-5' style={{cursor: 'pointer'}} onClick={handleDeselectClick}>
                    <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                  </div>

                  <div
                    className='m-1'
                    style={{cursor: 'pointer'}}
                    onClick={handleCheckedRemoveClick}
                  >
                    <button
                      className='btn btn-sm fs-6'
                      style={{backgroundColor: '#0E9F6E', color: '#FFFFFF', height: '40px'}}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#148363')
                      }
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#0E9F6E')
                      }
                    >
                      Remove Checklist
                    </button>
                  </div>
                </>
              ) : (
                <AddPersonnelChecklistModal currentAuditContext={currentAuditContext} />
              )}
            </div>
          </div>
          <div className='card-body py-3 '>
            <div className='table-responsive' style={{height: '55vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 '>
                <thead>
                  <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                    {/* <th className='w-25px'>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          value='selectAll'
                          data-kt-check='true'
                          data-kt-check-target='.widget-9-check'
                          onChange={handleSelectAllChange}
                        />
                      </div>
                    </th> */}
                    <th className='min-w-300px'>CHECKLIST</th>
                    <th className='min-w-150px'>NO. OF GROUPS</th>
                    <th className='min-w-150px'>CREATED ON</th>
                    <th className='min-w-150px'>CREATED BY</th>
                    <th className='min-w-50px'></th>
                  </tr>
                </thead>

                {filteredItems && filteredItems.length > 0 ? (
                  filteredItems
                    .filter((user) => user.tenantId === currentUser?.tenantId)
                    .map((item) => (
                      <tbody>
                        <tr>
                          {/* <td>
                            <div className='form-check form-check-sm form-check-custom form-check-solid'>
                              <input
                                className='form-check-input widget-9-check'
                                type='checkbox'
                                value={item.id}
                                checked={selectAll || checkedItems.includes(item.id)}
                                onChange={handleCheckChange}
                              />
                            </div>
                          </td> */}

                          <td>
                            <div className='d-flex align-items-center'>
                              <div className='d-flex align-items-center fw-bold'>
                                <div className='symbol symbol-20px me-15'>
                                  <div className=' fs-6'>
                                    <div>{item.checklist_name}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center justify-content-center fw-semibold'>
                              <div className='symbol symbol-20px me-15'>
                                <div className=' fs-6'>
                                  <div>{checklistCounts[item.checklist_name] || '-'}</div>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td>
                            <div className='d-flex align-items-center fw-semibold'>
                              <div className='symbol symbol-20px me-15'>
                                <div className=' fs-6'>
                                  <div>
                                    {item.createdon ? formatTimestamp(item.createdon) : '--'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center fw-semibold'>
                              <div className='symbol symbol-20px me-15'>
                                <div className=' fs-6'>
                                  <div>{currentUser?.userName}</div>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className=''>
                            <div className='dropdown mx-2 '>
                              {currentUser?.role !== 'Auditor' && !currentAuditContext && (
                                <button
                                  className='btn btn-secondary border-0 p-0 m-2'
                                  type='button'
                                  id='dropdownMenu2'
                                  data-bs-toggle='dropdown'
                                  aria-haspopup='true'
                                  aria-expanded='false'
                                >
                                  <img
                                    src='/media/buttons/Options.svg'
                                    alt='Options'
                                    onClick={toggleDropdown}
                                  />
                                </button>
                              )}
                              <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                                <li>
                                  <button
                                    className='dropdown-item text-hover-primary'
                                    type='button'
                                    style={{color: '#262626'}}
                                    onClick={() => {
                                      handleEditClick(item.id)
                                    }}
                                    disabled={
                                      currentUser?.role === 'Auditor' || !!currentAuditContext
                                    }
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      stroke-width='2'
                                      stroke-linecap='round'
                                      stroke-linejoin='round'
                                      className='icon icon-tabler icons-tabler-outline icon-tabler-edit mx-2 mb-1'
                                    >
                                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                      <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
                                      <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
                                      <path d='M16 5l3 3' />
                                    </svg>
                                    Edit Checklist
                                  </button>
                                </li>

                                <li>
                                  <button
                                    className='dropdown-item '
                                    type='button'
                                    style={{color: '#F15363'}}
                                    onClick={(e) => handleDeleteClick(item)}
                                    disabled={
                                      currentUser?.role === 'Auditor' || !!currentAuditContext
                                    }
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      stroke-width='2'
                                      stroke-linecap='round'
                                      stroke-linejoin='round'
                                      className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-2 mb-1'
                                    >
                                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                      <path d='M4 7l16 0' />
                                      <path d='M10 11l0 6' />
                                      <path d='M14 11l0 6' />
                                      <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                      <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                    </svg>{' '}
                                    Delete Checklist
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    ))
                ) : (
                  <tbody>
                    <tr style={{height: '100%'}}>
                      {searchLength ? (
                        <td colSpan={6} className='text-center'>
                          <div className='m-20'>
                            <img src='/media/buttons/emptystate.svg' alt='' />
                            <div className='m-5 fs-5'>
                              <p style={{fontWeight: 'bold'}}>No personnel Found</p>
                            </div>
                            <p className=''>
                              We couldn't find any checklist that match your search. Maybe try a
                              different search!
                            </p>
                          </div>
                        </td>
                      ) : (
                        <td colSpan={6} className='text-center'>
                          {showEmptyState && (
                            <div className='m-20'>
                              <img src='/media/buttons/emptystate.svg' alt='' />
                              <div className='m-5 fs-5'>
                                <p style={{fontWeight: 'bold'}}>No personnel Added</p>
                              </div>
                              <p>All checklist are required to adhere to.</p>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </KTCard>
      <ConfirmationModal
        isModalOpen={isBulkRemoveModalOpen}
        hideModal={() => setIsBulkRemoveModalOpen(false)}
        confirmBtnClickHandler={handleConfirmBulkRemove}
        title='Remove all Checklists except "Default"?'
        message='Are you sure you want to remove all checklists from the organization? This action cannot be undone.'
        confirmButtonText='Remove'
        type='delete'
        isLoading={isBulkRemoving}
      />
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        hideModal={() => setIsDeleteModalOpen(false)}
        confirmBtnClickHandler={handleConfirmDelete}
        toDeleteItemTitle={selectedItem?.checklist_name}
        isLoading={isDeleting}
        title={`Delete checklist '${selectedItem?.checklist_name}'?`}
        message={`Are you sure you want to delete the checklist '${selectedItem?.checklist_name}'? This action cannot be undone.`}
        confirmButtonText='Remove'
        type='delete'
      />
    </>
  )
}

export default PersonnelChecklist
